import React, { useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Avatar,
  useTheme,
  MenuItem,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../contexts/AuthContext";
import handleImagePreview from "../../../utils/imagePreview";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CustomTextField from "../../Credit/component/CustomTextField";
import createUser from "../../../api/users/create";
import { SpinnerContext } from "../../../contexts/SpinnerContext";
import { useSnackbar } from "../../../contexts/InfoContext";
import useFilteredCollectionData from "../../../hooks/useFilteredCollectionData";
import capitalizeString from "../../../utils/capitalizeString";
import { format } from "crypto-js";
import fetchData from "../../../api/services/Users/essential";
import useCollectionData from "../../../hooks/useCollectionData";

const FILE_SIZE = 160 * 1024;
const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/gif", "image/png"];

const validationSchema = Yup.object().shape({
  fullName: Yup.string().required("Full Name is required"),
  phone: Yup.string()
    .matches(/^\d+$/, "Phone must be a valid number")
    .required("Phone is required"),
  branches: Yup.array()
    .min(1, "At least one branch must be selected")
    .required("Branch selection is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  bankAccount: Yup.number()
    .positive("Bank Account must be a positive number")
    .typeError("Bank Account must be a number")
    .required("Bank Account is required"),
  fullAddress: Yup.string().required("Full Address is required"),
  securityName: Yup.string().required("Security Name is required"),
  securityAddress: Yup.string().required("Security Address is required"),
  securityPhone: Yup.string()
    .matches(/^\d+$/, "Security Phone must be a valid number")
    .required("Security Phone is required"),
  salary: Yup.number()
    .positive("Salary must be a positive number")
    .typeError("Salary must be a number")
    .required("Salary is required"),
  birthday: Yup.string().required("Date of Birth is required"),
  profileImage: Yup.mixed()
    .notRequired()
    .test(
      "fileFormat",
      "Unsupported Format. Only JPEG, PNG, and GIF images are supported",
      (value) => !value || SUPPORTED_FORMATS.includes(value?.type)
    )
    .test(
      "fileSize",
      "File too large",
      (value) => !value || value?.size <= FILE_SIZE
    ),
});

const SubAdminRegisterForm = () => {
  const [imagePreview, setImagePreview] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const { user, forgotPassword } = useAuth();
  const theme = useTheme();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const { data: branches } = useCollectionData("branches");
  const branchOptions =
    branches?.map((item) => ({
      name: item.name,
      id: item.id,
      active: item.active,
    })) || [];

  const handleBranchChange = (event) => {
    const { value } = event.target;
    const selectedBranchIds =
      typeof value === "string" ? value.split(",") : value;

    const selectedBranches = branchOptions.filter((branch) =>
      selectedBranchIds.includes(branch.id)
    );

    // Create branch objects with both id and name
    const branchObjects = selectedBranches.map((branch) => ({
      branchId: branch.id,
      branchName: branch.name,
    }));

    formik.setFieldValue("branches", branchObjects || []);
    formik.setFieldValue("numberOfBranches", branchObjects?.length || 0);
  };

  const handleFormSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const name = capitalizeString(values.fullName);
      values.fullName = name;

      const formData = new FormData();

      // Handle the new branches array structure
      if (!values.branches || values.branches.length === 0) {
        handleCloseForm();
        throw {
          response: {
            data: {
              message: "At least one branch must be selected",
              type: "error",
            },
          },
        };
      }

      // Append branches as object array (same format as edit form)
      formData.append("branches", JSON.stringify(values.branches));

      // Extract branch IDs and names for backward compatibility if needed
      const branchIds = values.branches?.map((branch) => branch.branchId) || [];
      const branchNames = values.branches?.map((branch) => branch.branchName) || [];
      formData.append("branchId", branchIds);
      formData.append("branchName", branchNames);

      // Append all other values to formData
      for (const key in values) {
        if (key === "branches") continue;
        else if (values[key] !== null && values[key] !== undefined) {
          formData.append(key, values[key]);
        }
      }

      formData.append("disable", false);

      const res = await createUser(user, formData, "subAdmin");
      forgotPassword(values.email);
      openSnackbar(res.data.message, "success");
      handleCloseForm();
    } catch (error) {
      if (error.response && error.response.data) {
        openSnackbar(
          error.response.data.message,
          error.response.data.type ? error.response.data.type : "error"
        );
      } else {
        openSnackbar(
          "An unexpected error occurred. Please kindly check your connection.",
          "error"
        );
      }
    }
    setIsSubmitting(false);
  };

  const formik = useFormik({
    initialValues: {
      fullName: "",
      phone: "",
      branches: [],
      numberOfBranches: 0,
      email: "",
      bankAccount: "",
      fullAddress: "",
      securityName: "",
      securityAddress: "",
      securityPhone: "",
      birthday: "",
      salary: "",
      active: "",
      profileImage: null,
    },
    validationSchema,
    onSubmit: handleFormSubmit,
  });

  const handleOpen = () => {
    setImagePreview(null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    formik.resetForm();
  };

  const handleImageChange = (e) => {
    handleImagePreview(e, setImagePreview);
    formik.setFieldValue("profileImage", e.currentTarget.files[0]);
  };

  return (
    <div>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create new Sub-Admin
      </Button>

      <Dialog open={showForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
          New SubAdmin
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                container
                alignItems="center"
                justifyContent="center"
              >
                <Grid item>
                  {imagePreview ? (
                    <Avatar
                      src={imagePreview}
                      sx={{ width: 100, height: 100 }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        backgroundColor: theme.palette.secondary[100],
                      }}
                    >
                      <AddPhotoAlternateIcon />
                    </Avatar>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <label
                  htmlFor="profileImage"
                  style={{
                    backgroundColor: theme.palette.background.alt,
                    padding: "10px 15px",
                    border: "1px solid #ccc",
                    cursor: "pointer",
                    display: "block",
                    width: "200px",
                    margin: "0 auto",
                    textAlign: "center",
                  }}
                >
                  Choose a profile image
                </label>
                <TextField
                  type="file"
                  name="profileImage"
                  id="profileImage"
                  accept="image/*"
                  variant="outlined"
                  fullWidth
                  onChange={(e) => {
                    formik.setFieldValue(
                      "profileImage",
                      e.currentTarget.files[0]
                    );
                    handleImageChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  style={{ display: "none" }}
                  error={
                    formik.touched.profileImage &&
                    Boolean(formik.errors.profileImage)
                  }
                  helperText={
                    formik.touched.profileImage && formik.errors.profileImage
                  }
                />
                {formik.touched.profileImage &&
                  Boolean(formik.errors.profileImage) && (
                    <div style={{ color: "red", marginTop: "20px" }}>
                      {formik.errors.profileImage}
                    </div>
                  )}
              </Grid>

              <CustomTextField
                label="Full Name"
                name="fullName"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Phone"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Bank Account"
                name="bankAccount"
                value={formik.values.bankAccount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Full Address"
                name="fullAddress"
                value={formik.values.fullAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Security Name"
                name="securityName"
                value={formik.values.securityName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Security Address"
                name="securityAddress"
                value={formik.values.securityAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Security Phone"
                name="securityPhone"
                value={formik.values.securityPhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Salary"
                name="salary"
                value={formik.values.salary}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
              />

              <CustomTextField
                label="Date Of Birth"
                type="date"
                name="birthday"
                value={formik.values.birthday}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Select Branches"
                  name="branches"
                  multiple
                  value={formik.values.branches?.map(
                    (branch) => branch.branchId
                  ) || []}
                  onChange={handleBranchChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.branches && Boolean(formik.errors.branches)
                  }
                  helperText={formik.touched.branches && formik.errors.branches}
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      marginTop: "8px",
                      color: theme.palette.secondary[100],
                    },
                  }}
                  SelectProps={{
                    multiple: true,
                    MenuProps: {
                      PaperProps: {
                        style: {
                          backgroundColor: theme.palette.background.alt,
                          color: "inherit",
                          maxHeight: 300,
                        },
                      },
                    },
                    renderValue: (selected) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          alignItems: "center",
                        }}
                      >
                        {selected.length === 0 ? (
                          <Typography variant="body2" color="textSecondary">
                            No branches selected
                          </Typography>
                        ) : (
                          selected.map((id, index) => {
                            const branch = branchOptions.find(
                              (b) => b.id === id
                            );
                            return (
                              <React.Fragment key={id}>
                                <Chip
                                  label={branch?.name || `Branch (${id})`}
                                  sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 500,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.primary.dark,
                                    },
                                  }}
                                  size="small"
                                />
                                {index < selected.length - 1 && (
                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "20px",
                                      backgroundColor: theme.palette.divider,
                                      mx: 0.5,
                                    }}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </Box>
                    ),
                  }}
                >
                  {branchOptions.length === 0 ? (
                    <MenuItem
                      disabled
                      sx={{
                        backgroundColor: theme.palette.background.alt,
                        color: theme.palette.secondary[100],
                        py: 1.5,
                        "&:not(:last-child)": {
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography variant="body2">
                          No branches found
                        </Typography>
                      </Box>
                    </MenuItem>
                  ) : (
                    branchOptions.map((option, index) => {
                      const isSelected = formik.values.branches?.some(
                        (branch) => branch.branchId === option.id
                      ) || false;

                      return (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          sx={{
                            backgroundColor: isSelected
                              ? theme.palette.primary.main
                              : theme.palette.background.alt,
                            color: isSelected
                              ? theme.palette.primary.contrastText
                              : theme.palette.secondary[100],
                            py: 1.5,
                            "&:not(:last-child)": {
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                            "&:hover": {
                              backgroundColor: isSelected
                                ? theme.palette.primary.dark
                                : theme.palette.primary.light,
                              color: isSelected
                                ? theme.palette.primary.contrastText
                                : theme.palette.primary.contrastText,
                            },
                            "&.Mui-selected": {
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {option.name}
                            </Typography>
                            {isSelected && (
                              <Box
                                sx={{
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor:
                                    theme.palette.primary.contrastText,
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })
                  )}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                container
                alignItems="end"
                justifyContent="end"
              >
                <DialogActions
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCloseForm}
                    sx={{
                      color: theme.palette.secondary[100],
                      "&:hover": {
                        backgroundColor: theme.palette.background.alt,
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      color: theme.palette.secondary[100],
                      "&:hover": {
                        backgroundColor: theme.palette.background.alt,
                      },
                    }}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubAdminRegisterForm;
